/**
 * `/` — the landing screen.
 *
 * Built to be useful to whoever is looking at it rather than impressive: every
 * card is behind the permission that makes its numbers readable, so an editor
 * with no `user.read` sees the libraries and their own trash, and an
 * administrator sees the rest. A dashboard full of skeletons that will never
 * fill in is worse than a short one.
 *
 * It deliberately shows **no aggregate the API does not already answer**. There
 * is no "total files" number here, because there is no endpoint for it and
 * counting them client-side would mean walking the tree.
 */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import LayersIcon from '@mui/icons-material/Layers';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Timestamp } from '../components/Timestamp';
import { useAsync } from '../hooks/useAsync';
import { useAuth } from '../auth/useAuth';
import { Slot } from '../modules/Slot';
import { listGroups, listRoles, listUsers } from '../api/identity';
import { listLibraries } from '../api/resources';
import { listAuditEvents } from '../api/audit';
import { listDeliveries } from '../api/notifications';

interface StatProps {
  icon: SvgIconComponent;
  label: string;
  value: number | undefined;
  to: string;
  hint?: string;
}

function Stat({ icon: Icon, label, value, to, hint }: StatProps) {
  return (
    <Card variant="outlined">
      <CardActionArea component={RouterLink} to={to} sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'action.selected',
              color: 'primary.main',
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {value === undefined ? <Skeleton width={40} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary">
                {hint}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation('system');
  const { user, can, isSuperadmin } = useAuth();

  // Two keys rather than a substituted "there": "Welcome, there" has no
  // grammatical equivalent in Czech, and forcing one produces nonsense.
  const firstName = user?.displayName.split(' ')[0];

  const libraries = useAsync(() => listLibraries(), []);
  // These four tiles are counts, and `page.total` is the count — so each asks
  // for a single row and reads the total off the envelope. They used to fetch
  // up to 500 rows and render `length`, which was the right number only until
  // there were more than 500 of something, and then silently stopped being.
  const COUNT_ONLY = { limit: 1 } as const;

  const users = useAsync(() => listUsers(COUNT_ONLY), [], { enabled: can('user.read') });
  const groups = useAsync(() => listGroups(COUNT_ONLY), [], { enabled: can('group.read') });
  const roles = useAsync(() => listRoles(COUNT_ONLY), [], { enabled: can('role.read') });
  const recent = useAsync(() => listAuditEvents({ limit: 8 }), [], { enabled: can('audit.read') });
  const pending = useAsync(() => listDeliveries({ status: 'pending', ...COUNT_ONLY }), [], {
    enabled: can('notification.read'),
  });

  return (
    <>
      <PageHeader
        title={
          firstName
            ? t('dashboard.welcome', { name: firstName })
            : t('dashboard.welcomeAnonymous')
        }
        description={
          isSuperadmin ? t('dashboard.superadminIntro') : t('dashboard.intro')
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          mb: 4,
        }}
      >
        <Stat
          icon={LayersIcon}
          label={t('dashboard.stat.libraries')}
          value={libraries.data?.libraries.length}
          to="/files"
        />
        {can('user.read') && (
          <Stat
            icon={PeopleIcon}
            label={t('dashboard.stat.users')}
            value={users.data?.page.total ?? undefined}
            to="/users"
          />
        )}
        {can('group.read') && (
          <Stat
            icon={GroupsIcon}
            label={t('dashboard.stat.groups')}
            value={groups.data?.page.total ?? undefined}
            to="/groups"
          />
        )}
        {can('role.read') && (
          <Stat
            icon={BadgeIcon}
            label={t('dashboard.stat.roles')}
            value={roles.data?.page.total ?? undefined}
            to="/roles"
          />
        )}
        {can('notification.read') && (
          <Stat
            icon={SendIcon}
            label={t('dashboard.stat.pendingDeliveries')}
            value={pending.data?.page.total ?? undefined}
            to="/notifications/deliveries"
            hint={t('dashboard.stat.pendingHint')}
          />
        )}
      </Box>

      {/* What modules have for this person — one panel to a row. With no
          contribution rendering anything the box is empty, and `:empty` drops
          its margin too. */}
      <Box sx={{ display: 'grid', gap: 2, mb: 4, '&:empty': { display: 'none' } }}>
        <Slot name="dashboard.sections" props={{}} />
      </Box>

      {can('audit.read') && (
        <Panel
          icon={<HistoryIcon fontSize="small" />}
          title={t('dashboard.recentActivity')}
          actions={
            <Typography
              component={RouterLink}
              to="/audit"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              {t('dashboard.wholeLog')}
            </Typography>
          }
        >

          <Stack spacing={1}>
            {recent.data === undefined
              ? Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} variant="rounded" height={32} />
                ))
              : recent.data.events.map((event) => (
                  <Stack
                    key={event.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0.25, sm: 1.5 }} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={event.event}
                      sx={{ fontFamily: 'monospace', fontSize: 11 }}
                    />
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {event.actor.label}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' · '}
                        {event.entityType}:{event.entityId}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <Timestamp value={event.occurredAt} />
                    </Typography>
                  </Stack>
                ))}
          </Stack>
        </Panel>
      )}
    </>
  );
}
